import * as ort from 'onnxruntime-web';

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DetectionResult {
  detected: boolean;
  boundingBox?: BoundingBox;
  confidence?: number;
  error?: string;
}

interface PreprocessResult {
  tensor: ort.Tensor;
  scale: number;
  padX: number;
  padY: number;
  originalWidth: number;
  originalHeight: number;
}

interface Detection {
  box: BoundingBox;
  confidence: number;
}

class DataMatrixDetector {
  private session: ort.InferenceSession | null = null;
  private isLoading = false;
  private loadError: string | null = null;
  private readonly modelInputSize = 640;
  private readonly confidenceThreshold = 0.5;
  private readonly iouThreshold = 0.45;

  async loadModel(): Promise<void> {
    if (this.session) return;
    if (this.isLoading) {
      while (this.isLoading) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (this.loadError) throw new Error(this.loadError);
      return;
    }

    this.isLoading = true;
    this.loadError = null;

    try {
      ort.env.wasm.wasmPaths = '/';
      
      this.session = await ort.InferenceSession.create(
        '/models/datamatrix_detector.onnx',
        {
          executionProviders: ['wasm'],
          graphOptimizationLevel: 'all',
        }
      );
      console.log('DataMatrixDetector: Model loaded successfully');
    } catch (error) {
      this.loadError = error instanceof Error ? error.message : 'Failed to load model';
      console.error('DataMatrixDetector: Failed to load model:', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  isModelLoaded(): boolean {
    return this.session !== null;
  }

  async detect(imageData: ImageData): Promise<DetectionResult> {
    if (!this.session) {
      return { detected: false, error: 'Model not loaded' };
    }

    try {
      const preprocessResult = this.letterboxPreprocess(imageData);
      const outputMap = await this.session.run({
        images: preprocessResult.tensor,
      });

      const outputTensor = outputMap[Object.keys(outputMap)[0]];
      return this.postProcess(outputTensor, preprocessResult);
    } catch (error) {
      console.error('DataMatrixDetector: Detection error:', error);
      return {
        detected: false,
        error: error instanceof Error ? error.message : 'Detection failed',
      };
    }
  }

  private letterboxPreprocess(imageData: ImageData): PreprocessResult {
    const { width: originalWidth, height: originalHeight, data: _data } = imageData;

    const scale = Math.min(
      this.modelInputSize / originalWidth,
      this.modelInputSize / originalHeight
    );

    const scaledWidth = Math.round(originalWidth * scale);
    const scaledHeight = Math.round(originalHeight * scale);

    const padX = (this.modelInputSize - scaledWidth) / 2;
    const padY = (this.modelInputSize - scaledHeight) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = this.modelInputSize;
    canvas.height = this.modelInputSize;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = 'rgb(114, 114, 114)';
    ctx.fillRect(0, 0, this.modelInputSize, this.modelInputSize);

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = originalWidth;
    tempCanvas.height = originalHeight;
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.putImageData(imageData, 0, 0);

    ctx.drawImage(tempCanvas, padX, padY, scaledWidth, scaledHeight);

    const letterboxedData = ctx.getImageData(
      0,
      0,
      this.modelInputSize,
      this.modelInputSize
    );

    const float32Data = new Float32Array(3 * this.modelInputSize * this.modelInputSize);
    const pixelCount = this.modelInputSize * this.modelInputSize;

    for (let i = 0; i < pixelCount; i++) {
      const srcIdx = i * 4;
      float32Data[i] = letterboxedData.data[srcIdx] / 255.0;
      float32Data[pixelCount + i] = letterboxedData.data[srcIdx + 1] / 255.0;
      float32Data[2 * pixelCount + i] = letterboxedData.data[srcIdx + 2] / 255.0;
    }

    const tensor = new ort.Tensor('float32', float32Data, [
      1,
      3,
      this.modelInputSize,
      this.modelInputSize,
    ]);

    return {
      tensor,
      scale,
      padX,
      padY,
      originalWidth,
      originalHeight,
    };
  }

  private postProcess(
    outputTensor: ort.Tensor,
    preprocessResult: PreprocessResult
  ): DetectionResult {
    const outputData = outputTensor.data as Float32Array;
    const shape = outputTensor.dims;

    let detections: Detection[] = [];

    if (shape.length === 3) {
      if (shape[1] === 84 && shape[2] === 8400) {
        detections = this.extractDetectionsYOLOv8(
          outputData,
          shape[2],
          preprocessResult
        );
      } else if (shape[1] === 5 || shape[1] === 6) {
        detections = this.extractDetectionsShape1VN(
          outputData,
          shape[2],
          shape[1],
          preprocessResult
        );
      } else if (shape[2] === 5 || shape[2] === 6) {
        detections = this.extractDetectionsShape1NV(
          outputData,
          shape[1],
          shape[2],
          preprocessResult
        );
      }
    }

    const nmsDetections = this.applyNMS(detections);

    if (nmsDetections.length === 0) {
      return { detected: false };
    }

    const best = nmsDetections[0];
    return {
      detected: true,
      boundingBox: best.box,
      confidence: best.confidence,
    };
  }

  private extractDetectionsYOLOv8(
    data: Float32Array,
    numDetections: number,
    preprocessResult: PreprocessResult
  ): Detection[] {
    const detections: Detection[] = [];
    const numClasses = 80;

    for (let i = 0; i < numDetections; i++) {
      const xc = data[0 * numDetections + i];
      const yc = data[1 * numDetections + i];
      const w = data[2 * numDetections + i];
      const h = data[3 * numDetections + i];

      let maxClassConf = 0;
      for (let c = 0; c < numClasses; c++) {
        const classConf = data[(4 + c) * numDetections + i];
        if (classConf > maxClassConf) {
          maxClassConf = classConf;
        }
      }

      if (maxClassConf >= this.confidenceThreshold) {
        const box = this.convertLetterboxToOriginal(
          xc / this.modelInputSize,
          yc / this.modelInputSize,
          w / this.modelInputSize,
          h / this.modelInputSize,
          preprocessResult
        );
        detections.push({ box, confidence: maxClassConf });
      }
    }

    return detections;
  }

  private extractDetectionsShape1VN(
    data: Float32Array,
    numDetections: number,
    valuesPerDetection: number,
    preprocessResult: PreprocessResult
  ): Detection[] {
    const detections: Detection[] = [];

    for (let i = 0; i < numDetections; i++) {
      const xc = data[0 * numDetections + i];
      const yc = data[1 * numDetections + i];
      const w = data[2 * numDetections + i];
      const h = data[3 * numDetections + i];

      let conf: number;
      if (valuesPerDetection === 6) {
        const objectness = data[4 * numDetections + i];
        const classConf = data[5 * numDetections + i];
        conf = objectness * classConf;
      } else {
        conf = data[4 * numDetections + i];
      }

      if (conf >= this.confidenceThreshold) {
        const box = this.convertLetterboxToOriginal(xc, yc, w, h, preprocessResult);
        detections.push({ box, confidence: conf });
      }
    }

    return detections;
  }

  private extractDetectionsShape1NV(
    data: Float32Array,
    numDetections: number,
    valuesPerDetection: number,
    preprocessResult: PreprocessResult
  ): Detection[] {
    const detections: Detection[] = [];

    for (let i = 0; i < numDetections; i++) {
      const baseIdx = i * valuesPerDetection;
      const xc = data[baseIdx];
      const yc = data[baseIdx + 1];
      const w = data[baseIdx + 2];
      const h = data[baseIdx + 3];

      let conf: number;
      if (valuesPerDetection === 6) {
        const objectness = data[baseIdx + 4];
        const classConf = data[baseIdx + 5];
        conf = objectness * classConf;
      } else {
        conf = data[baseIdx + 4];
      }

      if (conf >= this.confidenceThreshold) {
        const box = this.convertLetterboxToOriginal(xc, yc, w, h, preprocessResult);
        detections.push({ box, confidence: conf });
      }
    }

    return detections;
  }

  private convertLetterboxToOriginal(
    xc: number,
    yc: number,
    w: number,
    h: number,
    preprocessResult: PreprocessResult
  ): BoundingBox {
    const { scale, padX, padY, originalWidth, originalHeight } = preprocessResult;

    const xcPx = xc * this.modelInputSize;
    const ycPx = yc * this.modelInputSize;
    const wPx = w * this.modelInputSize;
    const hPx = h * this.modelInputSize;

    const x0InLetterbox = xcPx - wPx / 2;
    const y0InLetterbox = ycPx - hPx / 2;

    const x0Original = (x0InLetterbox - padX) / scale;
    const y0Original = (y0InLetterbox - padY) / scale;
    const widthOriginal = wPx / scale;
    const heightOriginal = hPx / scale;

    const x0Norm = x0Original / originalWidth;
    const y0Norm = y0Original / originalHeight;
    const widthNorm = widthOriginal / originalWidth;
    const heightNorm = heightOriginal / originalHeight;

    return {
      x: Math.max(0, Math.min(1, x0Norm)),
      y: Math.max(0, Math.min(1, y0Norm)),
      width: Math.max(0, Math.min(1 - Math.max(0, x0Norm), widthNorm)),
      height: Math.max(0, Math.min(1 - Math.max(0, y0Norm), heightNorm)),
    };
  }

  private applyNMS(detections: Detection[]): Detection[] {
    if (detections.length === 0) return [];

    const sorted = [...detections].sort((a, b) => b.confidence - a.confidence);
    const kept: Detection[] = [];

    while (sorted.length > 0) {
      const best = sorted.shift()!;
      kept.push(best);

      for (let i = sorted.length - 1; i >= 0; i--) {
        const iou = this.calculateIoU(best.box, sorted[i].box);
        if (iou > this.iouThreshold) {
          sorted.splice(i, 1);
        }
      }
    }

    return kept;
  }

  private calculateIoU(box1: BoundingBox, box2: BoundingBox): number {
    const x1 = Math.max(box1.x, box2.x);
    const y1 = Math.max(box1.y, box2.y);
    const x2 = Math.min(box1.x + box1.width, box2.x + box2.width);
    const y2 = Math.min(box1.y + box1.height, box2.y + box2.height);

    const intersectionWidth = Math.max(0, x2 - x1);
    const intersectionHeight = Math.max(0, y2 - y1);
    const intersectionArea = intersectionWidth * intersectionHeight;

    const area1 = box1.width * box1.height;
    const area2 = box2.width * box2.height;
    const unionArea = area1 + area2 - intersectionArea;

    return unionArea > 0 ? intersectionArea / unionArea : 0;
  }
}

export const dataMatrixDetector = new DataMatrixDetector();
